import * as React from 'react';
import { Eye } from 'lucide-react';
import { DocumentReader } from '../../word/api/DocumentReader';
import { detectTextDirection } from '../../utils/textUtils';
import './DocumentReviewResults.css';

interface ReviewFinding {
  paragraphIndex: number;
  explanation: {
    ourSideInterest: string;
    theirSideInterest: string;
    whyCritical?: string;
    whyWarning?: string;
    whyInfo?: string;
  };
}

interface ReviewFindings {
  critical: ReviewFinding[];
  warning: ReviewFinding[];
  info: ReviewFinding[];
}

interface DocumentReviewResultsProps {
  findings: ReviewFindings | null;
  expandedCategories: Set<string>;
  onToggleCategory: (category: string) => void;
  paragraphs: Array<{ index: number; text: string }>;
  onRestart?: () => void;
}

export const DocumentReviewResults: React.FC<DocumentReviewResultsProps> = ({
  findings,
  expandedCategories,
  onToggleCategory,
  paragraphs,
  onRestart,
}) => {
  const [expandedExplanations, setExpandedExplanations] = React.useState<Set<string>>(new Set());
  const [expandedParagraphs, setExpandedParagraphs] = React.useState<Set<string>>(new Set());
  const [isDarkMode, setIsDarkMode] = React.useState<boolean>(() => {
    // Check localStorage for saved preference, default to light
    try {
      const saved = localStorage.getItem('documentReviewTheme');
      return saved === 'dark';
    } catch {
      return false;
    }
  });
  
  // Helper to get paragraph text by index
  const getParagraphText = (index: number): string => {
    const paragraph = paragraphs.find(p => p.index === index);
    return paragraph ? paragraph.text : `[Paragraph ${index} not found]`;
  };

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    try {
      localStorage.setItem('documentReviewTheme', newTheme ? 'dark' : 'light');
    } catch (err) {
      console.error('Error saving theme preference:', err);
    }
  };

  const toggleExplanation = (key: string) => {
    setExpandedExplanations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const toggleParagraph = (key: string) => {
    setExpandedParagraphs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const navigateToParagraph = async (paragraphIndex: number) => {
    try {
      await DocumentReader.navigateToParagraph(paragraphIndex);
    } catch (err) {
      console.error('Failed to navigate to paragraph:', err);
    }
  };

  const renderFinding = (finding: ReviewFinding, category: string, index: number) => {
    const key = `${category}-${index}`;
    const isExplanationExpanded = expandedExplanations.has(key);
    const isParagraphExpanded = expandedParagraphs.has(key);
    const paragraphText = getParagraphText(finding.paragraphIndex);
    const truncatedText = paragraphText.length > 150 
      ? paragraphText.substring(0, 150) + '...' 
      : paragraphText;

    return (
      <div key={key} className="review-finding">
        <div className="finding-header">
          <button
            className="review-navigate-btn"
            onClick={() => navigateToParagraph(finding.paragraphIndex)}
            title="Go to paragraph in document"
          >
            <Eye size={12} />
          </button>
          <button
            className="explanation-badge"
            onClick={() => toggleExplanation(key)}
            title="Click to see explanation"
          >
            {isExplanationExpanded ? 'Hide' : 'Why?'}
          </button>
        </div>
        
        <div className="finding-content">
          <p className="paragraph-text" dir={detectTextDirection(paragraphText)}>
            {isParagraphExpanded ? paragraphText : truncatedText}
            {paragraphText.length > 150 && (
              <button
                className="show-more-btn"
                onClick={() => toggleParagraph(key)}
              >
                {isParagraphExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </p>
          
          {isExplanationExpanded && (
            <div className="explanation-content">
              <div className="explanation-section">
                <strong>Our Side Interest:</strong>
                <p dir={detectTextDirection(finding.explanation.ourSideInterest)}>
                  {finding.explanation.ourSideInterest}
                </p>
              </div>
              <div className="explanation-section">
                <strong>Their Side Interest:</strong>
                <p dir={detectTextDirection(finding.explanation.theirSideInterest)}>
                  {finding.explanation.theirSideInterest}
                </p>
              </div>
              <div className="explanation-section">
                <strong>Why This Matters:</strong>
                <p dir={detectTextDirection(
                  finding.explanation.whyCritical || 
                  finding.explanation.whyWarning || 
                  finding.explanation.whyInfo || 
                  ''
                )}>
                  {finding.explanation.whyCritical || finding.explanation.whyWarning || finding.explanation.whyInfo}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCategory = (
    category: 'critical' | 'warning' | 'info',
    title: string,
    icon: string,
    findings: ReviewFinding[]
  ) => {
    const isExpanded = expandedCategories.has(category);
    const count = findings.length;

    return (
      <div className={`review-category ${category}`}>
        <div 
          className="category-header"
          onClick={() => onToggleCategory(category)}
        >
          <span className="category-icon">{icon}</span>
          <span className="category-title">{title}</span>
          <span className="category-count">{count}</span>
          <span className={`category-caret ${isExpanded ? 'expanded' : ''}`}>{isExpanded ? '▼' : '▶'}</span>
        </div>
        
        {isExpanded && (
          <div className="category-content">
            {count === 0 ? (
              <div className="no-findings">No {title.toLowerCase()} findings</div>
            ) : (
              findings.map((finding, index) => renderFinding(finding, category, index))
            )}
          </div>
        )}
      </div>
    );
  };

  if (!findings) {
    return (
      <div className="review-results-container">
        <div className="no-findings">No review findings available</div>
      </div>
    );
  }

  return (
    <div className={`review-results-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="results-header">
        <h2>Document Review</h2>
        <div className="header-actions">
          {onRestart && (
            <button
              className="restart-button"
              onClick={onRestart}
              title="Restart review from the beginning"
              aria-label="Restart review"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                <path d="M3 21v-5h5"></path>
              </svg>
              <span>Restart</span>
            </button>
          )}
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            )}
          </button>
          <div className="info-icon" title="Review findings categorized by severity">
            ℹ️
          </div>
        </div>
      </div>
      
      <div className="categories-list">
        {renderCategory('critical', 'Critical', '●', findings.critical)}
        {renderCategory('warning', 'Warning', '▲', findings.warning)}
        {renderCategory('info', 'Info', '●', findings.info)}
      </div>
    </div>
  );
};

